import React, { useContext, useRef, useEffect, useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { Cookies } from '@confluence/cookies';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { cfetch } from '@confluence/network';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { AuthData } from '../../../services/GoogleAPIs/GoogleAuth.ts';
import { GoogleAuth } from '../../../services/GoogleAPIs/GoogleAuth.ts';
import type { GoogleDrivePickerDoc } from '../../../services/GoogleAPIs/GoogleDrivePicker.ts';
import { GoogleDrivePicker } from '../../../services/GoogleAPIs/GoogleDrivePicker.ts';
import { GoogleDrive } from '../../../services/GoogleAPIs/GoogleDrive.ts';
import { FILES_EXPORT_FORMATS } from '../../../config/fileTypes.ts';
import { GOOGLE_EXPORT_ERRORS } from '../../../config/googleExportErrors.ts';
import { containsRequiredError } from '../../../utils/containsRequiredError.tsx';
import { useModals } from '../../../hooks/useModals.ts';
import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import { ImportStateContext } from '../../ImportStateContextProvider.tsx';
import { ImportModalTypes } from '../../Modals/ImportModalTypes.ts';
import { checkForAbortErrorMsg, REQUEST_ABORTED } from '../../../utils/cfetchWithAbort.ts';
import { useIntl } from 'react-intl';

// oxlint-disable-next-line import/no-cycle
import { ActiveImports, IMPORT_FOLDER_NAMES } from '../importStrategyDefinitions.ts';
import { getErrorBody } from '../../../utils/getErrorBody.tsx';
import type { ImportStrategyComponentType } from '../../types.ts';
import { useBulkLimitCheck } from '../../../hooks/useBulkLimitCheck.ts';
import { useAddAbortControllerDocuments } from '../../../hooks/useAddAbortControllerDocuments.ts';
import { useOutboundAuth } from '../../../hooks/useOutboundAuth.ts';

import { GoogleCookiesModal } from './GoogleCookiesModal.tsx';
import { GoogleFileSizeModal } from './GoogleFileSizeModal.tsx';
import { GoogleSignInModal } from './GoogleSignInModal.tsx';
import { useGoogleBulkErrors } from './useGoogleBulkErrors.ts';

const pressableStyles = cssMap({
	root: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
});

type GoogleAuth = {
	appId: string;
	apiKey: string;
};

enum GDModalTypes {
	SIGN_IN = 'SIGN_IN',
	FILE_SIZE = 'FILE_SIZE',
	COOKIES = 'COOKIES',
}

const isJiraImportGoogleDocExperiment = createSingleQueryParamHook('importGDoc');
const useImportSourceParam = createSingleQueryParamHook('importSource');

export const GOOGLE_PICKER_THIRD_PARTY_KEY = 'google-picker';

export const ImportFromGD: ImportStrategyComponentType = ({
	onStartStrategy,
	onDocumentSelect,
	onDocumentSelectError,
	registerTrigger,
	skipAuthInterstitial,
	children,
}) => {
	const { formatMessage } = useIntl();
	const authData = useRef<AuthData | null>(null);
	const googleToken = useRef<string>();
	const outboundAuthContainer = useRef<string>();
	const googleAuthData = useRef<GoogleAuth | null>(null);

	const shouldRenderSignIn = isJiraImportGoogleDocExperiment();
	const importSourceParam = useImportSourceParam();
	const { isEnabled: isExperimentEnabled } = useTwcg2304ImportInOnboarding();

	const {
		modalStack: GDModalStack,
		showModal: showGDModal,
		closeModal: closeGDModal,
	} = useModals<GDModalTypes>();
	const {
		closeModal: closeImportModal,
		docActions: { uploadDocSize, updateDocuments },
	} = useContext(ImportStateContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleGoogleBulkErrors = useGoogleBulkErrors();
	const bulkLimitCheck = useBulkLimitCheck();
	const addAbortControllerDocuments = useAddAbortControllerDocuments();
	const getAccessToken = useOutboundAuth();
	const { userId } = useSessionData();

	const exportFromGoogleDrive = async (
		selectedDoc: GoogleDrivePickerDoc,
		isBulkUpload: boolean,
	) => {
		const [selectedDocAbortSignal] = addAbortControllerDocuments([selectedDoc]);
		const { signal }: { signal: AbortSignal } = selectedDocAbortSignal;

		try {
			const googleAuth = fg('cc_feature_discovery_ifs_fe')
				? {
						apiKey: googleAuthData.current?.apiKey || '',
						appId: '',
						oauthToken: googleToken.current!,
					}
				: authData.current!;

			const blob = await new GoogleDrive(googleAuth).exportFile(
				selectedDoc.id,
				FILES_EXPORT_FORMATS.WordDocument,
				signal,
			);

			const file = new File([blob], selectedDoc.name, {
				lastModified: selectedDoc.lastEditedUtc,
				type: blob.type,
			});
			uploadDocSize(file.name, file.size);
			return file;
		} catch (error) {
			if (!isBulkUpload) {
				return handleSingleErrors(error, selectedDoc.name);
			} else {
				await handleGoogleBulkErrors(error, selectedDoc.name);
				return null;
			}
		}
	};

	const pickDocument = async () => {
		await Cookies.checkThirdParty(
			GOOGLE_PICKER_THIRD_PARTY_KEY,
			async () => {
				if (authData.current) {
					void selectFileInGDPicker();
				} else if (skipAuthInterstitial) {
					// Do exactly what the sign-in modal's primary button does, without
					// asking the user to confirm an intent they already expressed.
					if (fg('cc_feature_discovery_ifs_fe')) {
						// The modal's hover/focus prefetch never ran, so make sure the
						// outbound auth container is resolved before handleAuth needs it.
						await getGoogleSecrets();
						await handleAuth();
					} else {
						void selectFileInGDPicker();
					}
				} else {
					showGDModal(GDModalTypes.SIGN_IN);
				}
			},
			async () => {
				showGDModal(GDModalTypes.COOKIES);
			},
		);
	};

	//do not validate files before export because they don't have size and type
	const skipValidation = (docs) => docs;

	const validateBulkBeforeUpload = async (documents) => {
		const filteredDocs = documents.filter((doc: File | null) => doc);
		//update the context with the exported docs before limit modal is shown
		updateDocuments(filteredDocs);
		return bulkLimitCheck(filteredDocs);
	};

	// TODO: create base strategy component and remove the duplication
	// due to stale closure we have to keep the props in a ref, so the methods are always up to date after rerender
	// another approach is to move ImportContextStrategy into every strategy and extend it with the props.
	const strategyProps = {
		key: ActiveImports.IMPORT_FROM_GD,
		folderName: formatMessage(IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_GD]),
		pickDocument,
		exportFile: exportFromGoogleDrive,
		validateSingleUploadBeforeProgress: skipValidation,
		validateBulkUploadBeforeProgress: skipValidation,
		validateBulkBeforeUpload,
	};

	const strategyPropsRef = useRef(strategyProps);
	strategyPropsRef.current = strategyProps;

	const getGoogleSecrets = useCallback(async () => {
		// optimistically fetch outbound container
		if (!outboundAuthContainer.current && fg('cc_feature_discovery_ifs_fe')) {
			try {
				const result = await cfetch(
					`/wiki/rest/internals/1.0/word-doc/get-google-secrets?secretType=OUTBOUND_AUTH`,
				).then((res) => res.json());
				if (result) {
					outboundAuthContainer.current = result.outboundAuthContainerId;
				}
			} catch (err) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'gdPicker',
						action: 'fetch',
						actionSubject: 'outboundAuthToken',
						attributes: {
							errMessage: err.message,
						},
					},
				}).fire();
				return;
			}
		}
	}, [createAnalyticsEvent]);

	useEffect(() => {
		/**
		 * An experiment gate will also be added once we know what the eventual
		 * name of the parameter should be.
		 *
		 * Example query parameters:
		 * ?inEditorTemplatesPanel=keep_open&contextPanelTabTarget=import&importGDoc=TEST-123
		 */
		if (shouldRenderSignIn && expValEquals('cc_fd_jira_issue_live_doc_prompt', 'isEnabled', true)) {
			// Start the strategy with context due to how this was originally built to handle stale closures
			if (!googleToken.current && !googleAuthData.current) {
				onStartStrategy(strategyPropsRef);
			}

			void getGoogleSecrets();
		}
	}, [shouldRenderSignIn, showGDModal, getGoogleSecrets, onStartStrategy, strategyPropsRef]);

	useEffect(() => {
		// Auto-trigger Google Docs import when the user arrives via the onboarding Import CTA
		// with importSource=gDoc in the URL (set by FooterCreateButtons → createPageContent).
		// Guard with the experiment hook so this only fires for users enrolled in the experiment.
		if (importSourceParam === ActiveImports.IMPORT_FROM_GD && isExperimentEnabled) {
			if (!googleToken.current && !googleAuthData.current) {
				onStartStrategy(strategyPropsRef);
			}
			void getGoogleSecrets();
		}
		// Run once on mount — importSourceParam is a stable query-param value
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClick = () => {
		// Clear cached credentials when starting from the left nav so the user always
		// re-authenticates before the picker opens, including skipAuthInterstitial routes.
		authData.current = null;
		googleToken.current = undefined;
		onStartStrategy(strategyPropsRef);
	};

	// Expose the same entry point the card's onItemClick uses, so consumers that render
	// their own trigger (e.g. the onboarding CTA dropdown) can start the import without
	// this component owning the clickable element. `prefetch` mirrors the hover/focus
	// warm-up on the card wrapper below, which never fires when there is no visible card.
	const handleClickRef = useRef(handleClick);
	handleClickRef.current = handleClick;
	useEffect(() => {
		registerTrigger?.({
			start: () => handleClickRef.current(),
			prefetch: () => void getGoogleSecrets(),
		});
	}, [registerTrigger, getGoogleSecrets]);

	const checkSizeErrorAndShowModal = async (error, documentName: string) => {
		const errorBody = await getErrorBody(error);
		if (containsRequiredError(errorBody, GOOGLE_EXPORT_ERRORS.FILE_SIZE_LIMIT)) {
			closeImportModal(ImportModalTypes.PROGRESS);
			showGDModal(GDModalTypes.FILE_SIZE, {
				fileName: documentName,
			});
			return true;
		}

		return false;
	};

	const handleSingleErrors = async (error, docName: string) => {
		const handledError =
			error === REQUEST_ABORTED ||
			checkForAbortErrorMsg(error) ||
			(await checkSizeErrorAndShowModal(error, docName));

		if (!handledError) {
			onDocumentSelectError();
		}

		return null;
	};

	const sendGPickerCancelEvent = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'gdPicker',
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'gdPickerClose',
			},
		}).fire();
	};

	const sendGPickerSelectEvent = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'gdPicker',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gdPickerSelect',
			},
		}).fire();
	};

	const selectFileInGDPicker = async () => {
		try {
			if (!authData.current) {
				const data = await GoogleAuth.getAuthData();
				if (!data.oauthToken) return false;
				authData.current = data;
			}

			const gdPicker = await new GoogleDrivePicker(authData.current).init();

			const selectedDocs: GoogleDrivePickerDoc[] | null = await gdPicker.showPicker();

			if (!selectedDocs) {
				// absence of a selected doc means that picker was closed with Cancel/Close button
				sendGPickerCancelEvent();
				return;
			}

			sendGPickerSelectEvent();
			onDocumentSelect(selectedDocs);
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'gdPicker',
					action: 'select',
					actionSubject: 'googlDriveFile',
					attributes: {
						errMessage: error.message,
					},
				},
			}).fire();

			onDocumentSelectError();
		}
	};

	const closeModalAndSelectFile = (modalType: GDModalTypes) => {
		closeGDModal(modalType);
		void selectFileInGDPicker();
	};

	const handleFileSizeModalClose = () => {
		closeGDModal(GDModalTypes.FILE_SIZE);
		authData.current = null;
	};

	const handleAuth = async () => {
		closeGDModal(GDModalTypes.SIGN_IN);

		try {
			const result = await cfetch(
				`/wiki/rest/internals/1.0/word-doc/get-google-secrets?secretType=API_SECRETS`,
			).then((res) => res.json());
			// fetch and store appId and apiKey (for upload)
			if (result) {
				googleAuthData.current = {
					apiKey: result.googleApiKey,
					appId: result.googleAppId,
				};
			}
		} catch (err) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'gdPicker',
					action: 'fetch',
					actionSubject: 'googleDriveSecrets',
					attributes: {
						errMessage: err.message,
					},
				},
			}).fire();
			return;
		}

		void selectFile();
	};

	const getGoogleToken = async () => {
		// Fetch container ID by environment
		if (outboundAuthContainer.current && !googleToken.current && userId) {
			const token = await getAccessToken(outboundAuthContainer.current, 'gdrive', userId);

			googleToken.current = token;
		}
	};

	const selectFile = async () => {
		await getGoogleToken();

		// Fetch Google secrets (appId and apiKey)
		if (googleToken.current && googleAuthData.current) {
			const gdPicker = await new GoogleDrivePicker({
				oauthToken: googleToken.current,
				appId: googleAuthData.current.appId,
				apiKey: googleAuthData.current.apiKey,
			}).init();

			const selectedDocuments = await gdPicker.showPickerWithoutKey();

			// Upload selected documents
			if (selectedDocuments) {
				onDocumentSelect(selectedDocuments);
			}
		}
	};

	return (
		<>
			{GDModalStack[GDModalTypes.SIGN_IN] && (
				<GoogleSignInModal
					onClose={() => closeGDModal(GDModalTypes.SIGN_IN)}
					onSignIn={() => closeModalAndSelectFile(GDModalTypes.SIGN_IN)}
					handleAuth={handleAuth}
				/>
			)}
			{GDModalStack[GDModalTypes.FILE_SIZE] && (
				<GoogleFileSizeModal
					onClose={handleFileSizeModalClose}
					onSubmit={() => closeModalAndSelectFile(GDModalTypes.FILE_SIZE)}
					fileName={GDModalStack[GDModalTypes.FILE_SIZE].fileName}
				/>
			)}
			{GDModalStack[GDModalTypes.COOKIES] && (
				<GoogleCookiesModal onClose={() => closeGDModal(GDModalTypes.COOKIES)} />
			)}
			<div css={pressableStyles.root} onMouseEnter={getGoogleSecrets} onFocus={getGoogleSecrets}>
				{React.cloneElement(children as React.ReactElement, {
					...(children as React.ReactElement).props,
					onItemClick: (data: any) => {
						handleClick();
						const originalOnItemClick = (children as React.ReactElement).props.onItemClick;
						if (originalOnItemClick) {
							originalOnItemClick(data);
						}
					},
				})}
			</div>
		</>
	);
};
